import { z } from 'zod';
import { routeStatusSchema, routeTypeSchema } from '../../common/schemas/enum.schemas.js';
import { idParamSchema, uuidSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortInputSchema } from '../../common/schemas/pagination.schemas.js';
export const MAX_ROUTE_DISTANCE_KM = 10_000;
export const MAX_ELEVATION_M = 10_000;
export const routeSchema = z.object({
    id: uuidSchema,
    code: z.string().min(1),
    name: z.string().min(1),
    routeType: routeTypeSchema,
    version: z.number().int().min(1),
    /**
     * `null` for a route nobody has surveyed yet. A distance is what the `distance_completed` validity rule
     * measures a run against, so an unmeasured route makes that rule inapplicable rather than making its
     * runs invalid (see the validity engine's `min_pct_of` handling).
     */
    distanceKm: z.number().nullable(),
    /**
     * Cumulative ascent and descent, counted independently and never netted: a round trip records its whole
     * climb *and* its whole descent, which is why the two are separate columns rather than one signed figure.
     */
    elevationGainM: z.number().int().nullable(),
    elevationLossM: z.number().int().nullable(),
    /** How far of the route is spent climbing / descending, so an average grade is derivable from the pair. */
    ascentDistanceKm: z.number().nullable(),
    descentDistanceKm: z.number().nullable(),
    /**
     * `↑ 1,330 m over 26 km (5.1%) · ↓ 1,330 m over 26 km (−5.1%)` — formatted once, server-side, so the
     * routes table, a run's route panel and the printed report all read the same line. `null` when neither
     * half was surveyed; each half is omitted when its own figures are missing.
     */
    elevationProfile: z.string().nullable(),
    description: z.string().nullable(),
    status: routeStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const ROUTE_SORT_FIELDS = ['code', 'name', 'routeType', 'version', 'distanceKm', 'createdAt'];
export const routeSortBySchema = z.enum(ROUTE_SORT_FIELDS);
export const listRoutesInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: routeSortBySchema.default('code'),
    search: z.string().trim().min(1).optional(),
    routeType: routeTypeSchema.optional(),
    status: routeStatusSchema.optional(),
});
export const listRoutesOutputSchema = paginatedSchema(routeSchema);
export const createRouteInputSchema = z.object({
    code: z
        .string()
        .trim()
        .min(1, 'Code is required')
        .max(64)
        .regex(/^[A-Za-z0-9][A-Za-z0-9-]*$/, 'Use letters, digits and hyphens only'),
    name: z.string().trim().min(1, 'Name is required').max(255),
    routeType: routeTypeSchema,
    /** Optional: a route can be catalogued before it is measured, and surveyed into a new version later. */
    distanceKm: z.coerce.number().positive().max(MAX_ROUTE_DISTANCE_KM).optional(),
    /** Total climb and the distance it is spread over; the same pair again for the descent. Never netted. */
    elevationGainM: z.coerce.number().int().min(0).max(MAX_ELEVATION_M).optional(),
    ascentDistanceKm: z.coerce.number().positive().max(MAX_ROUTE_DISTANCE_KM).optional(),
    elevationLossM: z.coerce.number().int().min(0).max(MAX_ELEVATION_M).optional(),
    descentDistanceKm: z.coerce.number().positive().max(MAX_ROUTE_DISTANCE_KM).optional(),
    description: z.string().trim().max(2000).optional(),
});
/** A measurable change to a route is a new revision, never an edit (plan §4). */
export const newRouteVersionInputSchema = z.object({
    ...idParamSchema.shape,
    name: z.string().trim().min(1).max(255).optional(),
    /** An explicit `null` un-measures the route again; an absent key carries the old distance forward. */
    distanceKm: z.coerce.number().positive().max(MAX_ROUTE_DISTANCE_KM).nullish(),
    elevationGainM: z.coerce.number().int().min(0).max(MAX_ELEVATION_M).nullish(),
    ascentDistanceKm: z.coerce.number().positive().max(MAX_ROUTE_DISTANCE_KM).nullish(),
    elevationLossM: z.coerce.number().int().min(0).max(MAX_ELEVATION_M).nullish(),
    descentDistanceKm: z.coerce.number().positive().max(MAX_ROUTE_DISTANCE_KM).nullish(),
    description: z.string().trim().max(2000).nullish(),
});
/** Editing an existing revision is limited to descriptive fields; measurements need a new version. */
export const updateRouteInputSchema = z.object({
    ...idParamSchema.shape,
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).nullish(),
});
//# sourceMappingURL=routes.schemas.js.map